/**
 * SID and STAR geometry shared by the briefing charts and the radar overlays: grouping by
 * runway, the lines a chart draws, resolved lat/lon points and restriction text. Pure; no DOM.
 */
import { findRunwayEnd, oppositeEnd, type AirportData, type LatLon, type Procedure, type Restriction, type RouteLeg } from './airport';
import { destination, NM_TO_M } from './geo/projection';

export interface ProcedureGroup {
  /** Runway name, or '' for procedures without runway-specific legs. */
  runway: string;
  sids: Procedure[];
  stars: Procedure[];
}

/** Runway name from a procedure key such as "LLBG26" or "KSFO28L". */
export const runwayOfKey = (airport: AirportData, key: string): string => (key.startsWith(airport.icao) ? key.slice(airport.icao.length) : key);

export function proceduresByRunway(airport: AirportData): ProcedureGroup[] {
  const groups = new Map<string, ProcedureGroup>();
  const add = (runway: string, p: Procedure) => {
    const g = groups.get(runway) ?? { runway, sids: [], stars: [] };
    (p.kind === 'sid' ? g.sids : g.stars).push(p);
    groups.set(runway, g);
  };
  for (const p of [...airport.sids.values(), ...airport.stars.values()]) {
    const keys = Object.keys(p.rwy);
    if (keys.length === 0) add('', p);
    for (const k of keys) add(runwayOfKey(airport, k), p);
  }
  const byName = (a: Procedure, b: Procedure) => a.name.localeCompare(b.name);
  return [...groups.values()]
    .map((g) => ({ ...g, sids: g.sids.sort(byName), stars: g.stars.sort(byName) }))
    .sort((a, b) => (a.runway === '' ? 1 : b.runway === '' ? -1 : a.runway.localeCompare(b.runway, undefined, { numeric: true })));
}

export interface ChartLine {
  legs: RouteLeg[];
  /** The runway legs and common body are 'main'; each entry or exit transition is 'transition'. */
  emphasis: 'main' | 'transition';
  /** Transition name (its entry or exit fix). */
  label?: string;
}

/**
 * Lines for a procedure flown from or to one runway (`rwyKey` like "LLBG26"): SID = runway legs,
 * body, then each exit transition; STAR = each entry transition, body, then the runway legs.
 * Transitions start or end at the body's first or last fix so they join the main line.
 */
export function procedureLines(p: Procedure, rwyKey: string | undefined): ChartLine[] {
  const rw = rwyKey ? p.rwy[rwyKey] ?? [] : [];
  const out: ChartLine[] = [];
  if (p.kind === 'sid') {
    const main = [...rw, ...p.body];
    out.push({ legs: main, emphasis: 'main' });
    const joinFix = [...main].reverse().find((l) => l.kind === 'fix');
    for (const [exit, legs] of Object.entries(p.exitPoints)) out.push({ legs: joinFix ? [joinFix, ...legs] : legs, emphasis: 'transition', label: exit });
  } else {
    const main = [...p.body, ...rw];
    out.push({ legs: main, emphasis: 'main' });
    const joinFix = main.find((l) => l.kind === 'fix');
    for (const [entry, legs] of Object.entries(p.entryPoints)) out.push({ legs: joinFix ? [...legs, joinFix] : legs, emphasis: 'transition', label: entry });
  }
  return out.filter((l) => l.legs.length > 0);
}

/** Chart style: plain digits, no thousands separator (it reads as a gap in the monospace chart font). */
const feet = (n: number) => String(Math.round(n));

/** "≥7000 ≤250 kt": at-or-above / at-or-below / at, altitudes in feet, speeds in knots. */
export function formatRestriction(r: Restriction | undefined): string {
  if (!r) return '';
  const parts: string[] = [];
  if (r.altitudeFt !== undefined) parts.push(feet(r.altitudeFt));
  if (r.altitudeMinFt !== undefined) parts.push(`≥${feet(r.altitudeMinFt)}`);
  if (r.altitudeMaxFt !== undefined) parts.push(`≤${feet(r.altitudeMaxFt)}`);
  if (r.speedKt !== undefined) parts.push(`${r.speedKt} kt`);
  if (r.speedMinKt !== undefined) parts.push(`≥${r.speedMinKt} kt`);
  if (r.speedMaxKt !== undefined) parts.push(`≤${r.speedMaxKt} kt`);
  return parts.join(' ');
}

/** Scope label style: altitudes in hundreds of feet like a data block, "≥60 ≤80 ≤230kt". */
export function formatRestrictionScope(r: Restriction | undefined): string {
  if (!r) return '';
  const h = (n: number) => String(Math.round(n / 100)).padStart(3, '0');
  const parts: string[] = [];
  if (r.altitudeFt !== undefined) parts.push(h(r.altitudeFt));
  if (r.altitudeMinFt !== undefined) parts.push(`≥${h(r.altitudeMinFt)}`);
  if (r.altitudeMaxFt !== undefined) parts.push(`≤${h(r.altitudeMaxFt)}`);
  if (r.speedKt !== undefined) parts.push(`${r.speedKt}kt`);
  if (r.speedMinKt !== undefined) parts.push(`≥${r.speedMinKt}kt`);
  if (r.speedMaxKt !== undefined) parts.push(`≤${r.speedMaxKt}kt`);
  return parts.join(' ');
}

/** Length of the stub drawn for a heading leg (the aircraft is then vectored). */
export const HEADING_STUB_NM = 5;

export interface ProcedureGeometry {
  lines: { points: LatLon[]; emphasis: 'main' | 'transition'; label?: string }[];
  /** Each fix once, with the restriction a chart labels (the first non-empty one seen). */
  fixes: { name: string; at: LatLon; restriction?: Restriction }[];
  /** End points of heading-leg stubs. */
  headings: { at: LatLon; headingDeg: number }[];
  /** Table rows in flying order; fixes repeated on a transition are listed once. */
  rows: ({ fix: string; restriction: string; transition?: string } | { headingDeg: number; transition?: string })[];
}

/**
 * Resolved geometry for one procedure at one runway. A SID starts where the aircraft lifts off
 * (the far end of the departure runway); a STAR's main line ends at the landing threshold;
 * heading legs become short stubs.
 */
export function procedureGeometry(airport: AirportData, p: Procedure, rwyKey: string | undefined): ProcedureGeometry {
  const rwName = rwyKey ? runwayOfKey(airport, rwyKey) : undefined;
  const threshold = rwName ? findRunwayEnd(airport, rwName) : undefined;
  const liftOff = rwName ? oppositeEnd(airport, rwName) : undefined;
  const out: ProcedureGeometry = { lines: [], fixes: [], headings: [], rows: [] };
  const fixes = new Map<string, { at: LatLon; restriction?: Restriction; text: string }>();
  for (const line of procedureLines(p, rwyKey)) {
    const points: LatLon[] = [];
    let cur: LatLon | undefined;
    const transition = line.label;
    if (p.kind === 'sid' && line.emphasis === 'main' && liftOff) { points.push(liftOff); cur = liftOff; }
    for (const leg of line.legs) {
      if (leg.kind === 'fix') {
        const text = formatRestriction(leg.restriction);
        if (line.emphasis === 'main' || !fixes.has(leg.fix)) out.rows.push({ fix: leg.fix, restriction: text, ...(transition ? { transition } : {}) });
        const fx = airport.fixes.get(leg.fix);
        if (!fx) continue;
        points.push(fx);
        cur = fx;
        const prev = fixes.get(leg.fix);
        if (!prev || (text && !prev.text)) fixes.set(leg.fix, { at: fx, restriction: leg.restriction, text });
      } else {
        out.rows.push({ headingDeg: leg.headingDeg, ...(transition ? { transition } : {}) });
        if (!cur) continue;
        const to = destination(cur.lat, cur.lon, leg.headingDeg, HEADING_STUB_NM * NM_TO_M);
        points.push(to);
        out.headings.push({ at: to, headingDeg: leg.headingDeg });
        cur = to;
      }
    }
    if (p.kind === 'star' && line.emphasis === 'main' && threshold) points.push(threshold);
    if (points.length >= 2) out.lines.push({ points, emphasis: line.emphasis, ...(transition ? { label: transition } : {}) });
  }
  out.fixes = [...fixes].map(([name, v]) => ({ name, at: v.at, ...(v.restriction ? { restriction: v.restriction } : {}) }));
  return out;
}
